import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { BookmarkPlus } from "lucide-react";
import { cn } from "../../lib/cn";
import { BlockReceiptView, readReceipt, type BlockReceipt } from "./blockReceipt";
import { BLOCK_SURFACE_PADDING } from "./blockSurface";

/**
 * MemorySave - a "save this to memory?" affordance. Renderer for the K2
 * `memory_save` block, one of the post-answer signals the runtime's response
 * post-processor emits (ais-agent-runtime `PostProcessSignals`,
 * AISquare-Studio/ais-agent-runtime#115; BE relays the
 * `memory_save_suggestion` frame, persistence lands via BE#2517). The
 * platform's chat `BlockRenderer` passes a `memory_save` block's `props`
 * straight through, so the prop names match the block payload exactly.
 * INTERACTIVE.
 *
 * After an answer, the runtime may detect a durable fact worth remembering (a
 * preference, a name, a recurring constraint) and surface it as an opt-in
 * "save this?" nudge - never an auto-write. It is gentle, not an
 * error/warning: low-contrast muted surface, hairline border, no destructive
 * colour.
 *
 * Block props (`{ text, submit_to?, receipt? }`):
 *   - `text` - the memory candidate, the fact phrased as it would be stored.
 *     Required.
 *   - `submit_to?` - destination routing token (mirrors `form.submit_to`),
 *     carried on the emitted submit event. Optional - a host may route by
 *     `block_id` + `type` alone (AIStudio-v3's signal-block handler does).
 *   - `receipt?` - once acted on, the durable settled state
 *     (`{ outcome, summary, identifiers?, at }`). When present the settled
 *     receipt renders instead of the live affordance, so a history reload shows
 *     "saved to memory" rather than re-offering a live, un-actioned control.
 *
 * On accept / dismiss the block emits the shared block-submit event keyed by
 * `block_id` + the chosen action (`"save"` carries the candidate text as the
 * value; `"dismiss"` carries no value), via `blockSubmit.tsx` - the same
 * transport `form` / `approval` / `suggestions` use, no new mechanism.
 *
 * Client-only props (never part of the streamed block): `className`, `onSave`,
 * `onDismiss` (wired by the block adapter to the submit context), `disabled`.
 *
 * Token-themed with no hardcoded colors. A11y: a labelled `role="group"` so
 * the candidate + its actions read as one unit. SSR-safe: no `window` /
 * `document` at module top or during render.
 */

const memorySaveVariants = cva(
  cn(
    // Muted-signal variant of the shared block surface (blockSurface.ts): same
    // radius + hairline + inset as BLOCK_SURFACE, but a low-emphasis muted
    // fill replaces the card fill - a nudge, not a content card.
    "not-prose flex flex-col gap-2.5 rounded-2xl border border-border/70 bg-muted/40",
    BLOCK_SURFACE_PADDING,
    "sm:flex-row sm:items-center sm:justify-between sm:gap-3",
  ),
);

const memorySaveActionVariants = cva(
  cn(
    "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium",
    "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
    "disabled:cursor-not-allowed disabled:opacity-50",
  ),
  {
    variants: {
      intent: {
        save: "bg-primary text-primary-foreground hover:bg-primary/90",
        dismiss: "text-muted-foreground hover:bg-accent/10 hover:text-foreground",
      },
    },
    defaultVariants: { intent: "save" },
  },
);

export interface MemorySaveProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, "onChange">,
    VariantProps<typeof memorySaveVariants> {
  /** The memory candidate - the fact phrased as it would be stored. Required. */
  text: string;
  /** Lead-in label above the candidate. Defaults to "Save this to memory?". */
  prompt?: string;
  /** Save button copy. Defaults to "Save". */
  saveLabel?: string;
  /** Dismiss button copy. Defaults to "Not now". */
  dismissLabel?: string;
  /** Settled state - renders the receipt instead of the live affordance when present. */
  receipt?: BlockReceipt | unknown;
  /** Accept handler. Wired by the block adapter to the submit context. */
  onSave?: (text: string) => void;
  /** Dismiss handler. Wired by the block adapter to the submit context. */
  onDismiss?: () => void;
  /** Disable interaction (e.g. while the save is in flight). */
  disabled?: boolean;
}

export const MemorySave = React.forwardRef<HTMLDivElement, MemorySaveProps>(function MemorySave(
  {
    className,
    text,
    prompt = "Save this to memory?",
    saveLabel = "Save",
    dismissLabel = "Not now",
    receipt,
    onSave,
    onDismiss,
    disabled = false,
    ...rest
  },
  ref,
) {
  const settled = readReceipt(receipt);

  if (settled) {
    return (
      <div
        ref={ref}
        className={cn("not-prose", className)}
        data-slot="memory-save"
        data-settled="true"
        {...rest}
      >
        <BlockReceiptView receipt={settled} />
      </div>
    );
  }

  return (
    <div
      ref={ref}
      role="group"
      aria-label={prompt}
      className={cn(memorySaveVariants(), className)}
      data-slot="memory-save"
      {...rest}
    >
      <div className="flex min-w-0 items-start gap-2 sm:items-center">
        <BookmarkPlus
          className="mt-0.5 size-4 shrink-0 text-muted-foreground sm:mt-0"
          aria-hidden
        />
        <div className="min-w-0">
          <p className="text-xs font-medium text-muted-foreground">{prompt}</p>
          <p className="text-sm leading-snug text-foreground">{text}</p>
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-1.5 self-end sm:self-auto">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onSave?.(text)}
          className={memorySaveActionVariants({ intent: "save" })}
        >
          {saveLabel}
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onDismiss?.()}
          className={memorySaveActionVariants({ intent: "dismiss" })}
        >
          {dismissLabel}
        </button>
      </div>
    </div>
  );
});

MemorySave.displayName = "MemorySave";

export { memorySaveVariants, memorySaveActionVariants };
export type MemorySaveVariantProps = VariantProps<typeof memorySaveVariants>;
